import { type WhiteboardRenderState, applyWhiteboardUpdates } from "../boardState";
import {
  type WhiteboardSpec,
  WhiteboardSpecSchema,
  type WhiteboardUpdateOp,
  normalizeAnswer,
} from "../spec";

export type NativeExercise = {
  spec: WhiteboardSpec;
  renderState: WhiteboardRenderState;
  readingEnd: boolean;
};
const fields: Record<string, string[]> = {
  gap_fill: ["kind", "id", "sentence", "options", "answer", "translation", "hint", "explanation"],
  quick_choice: ["kind", "id", "question", "options", "answerIndex", "explanation"],
  word_tiles: ["kind", "id", "tiles", "answer", "translation", "hint"],
  reading_story: ["kind", "id", "title", "story"],
  composed: ["kind", "id", "title", "blocks"],
};
export function parseNative(value: unknown): WhiteboardSpec {
  if (!value || typeof value !== "object" || !("kind" in value)) throw new Error("Native fields");
  const allowed = fields[String(value.kind)];
  if (!allowed || Object.keys(value).some((key) => !allowed.includes(key)))
    throw new Error("Native fields");
  const bound = (item: unknown, depth = 0) => {
    if (depth > 8) throw new Error("Native depth");
    if (typeof item === "string" && item.length > (value.kind === "reading_story" ? 4000 : 900))
      throw new Error("Native text");
    if (Array.isArray(item) && item.length > 12) throw new Error("Native collection");
    if (item && typeof item === "object")
      Object.values(item).forEach((child) => bound(child, depth + 1));
  };
  bound(value);
  const spec = WhiteboardSpecSchema.parse(value);
  const checkChoice = (answer: string, options: string[]) => {
    if (!options.some((option) => option.trim().toLowerCase() === answer.trim().toLowerCase()))
      throw new Error("Answer must be in choices");
  };
  if (spec.kind === "gap_fill") checkChoice(spec.answer, spec.options);
  if (spec.kind === "composed") {
    for (const block of spec.blocks) {
      if (block.type === "listening_discrimination") checkChoice(block.answer, block.options);
      if (block.type === "translation_choice")
        checkChoice(
          block.answer,
          block.options.map((option) => option.text)
        );
      if (block.type === "transformation_frame")
        block.items.forEach((item) => checkChoice(item.answer, item.options));
      if (block.type === "morphology_grid")
        block.rows.forEach((row) =>
          row.cells.forEach((cell) => checkChoice(cell.answer, cell.options))
        );
    }
  }
  if (spec.kind === "word_tiles") {
    const joined = spec.tiles.join(" ");
    if (
      normalizeAnswer(joined).split(" ").sort().join(" ") !==
      normalizeAnswer(spec.answer).split(" ").sort().join(" ")
    )
      throw new Error("Tiles must assemble the answer");
    if (normalizeAnswer(joined) === normalizeAnswer(spec.answer))
      spec.tiles = [...spec.tiles.slice(1), spec.tiles[0]];
  }
  if (spec.kind === "composed" && spec.blocks[0]?.type === "pair_match") {
    const block = spec.blocks[0];
    const left = block.leftItems.map((v) => v.id),
      right = block.rightItems.map((v) => v.id);
    if (
      new Set(left).size !== left.length ||
      new Set(right).size !== right.length ||
      [...left].sort().join("|") !==
        block.rightItems
          .map((v) => v.leftId)
          .sort()
          .join("|")
    )
      throw new Error("Pairs must match one to one");
  }
  if (
    spec.kind === "composed" &&
    (spec.blocks.length !== 1 ||
      ["text", "reveal", "sentence_repair"].includes(spec.blocks[0].type))
  )
    throw new Error("Native block");
  return spec;
}
export const visualOps = new Set([
  "highlight",
  "mark",
  "clear_marks",
  "connect",
  "clear_connections",
  "draw",
  "clear_drawings",
  "annotate",
  "add_element",
  "remove_element",
  "clear_elements",
  "set_hint",
]);
export function annotateNative(
  unit: NativeExercise,
  operations: WhiteboardUpdateOp[]
): NativeExercise {
  if (unit.spec.kind === "reading_story" || operations.some((op) => !visualOps.has(op.op)))
    throw new Error("Immutable exercise");
  return { ...unit, ...applyWhiteboardUpdates(unit.spec, unit.renderState, operations) };
}
